import { useCallback, useEffect, useRef, useState } from 'react';
import Quagga, { type QuaggaJSResultObject } from '@ericblade/quagga2';
import { Camera, Loader2, RefreshCw, X } from 'lucide-react';
import { normalizeBarcode, useTranslation } from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  SCANNER_MODES,
  SCANNER_MODE_STORAGE_KEY,
  addSample,
  analyzeQuaggaResult,
  buildQuaggaConfig,
  getBestSampleProgress,
  getDominantSample,
  getScannerMode,
  isScannerModeId,
  type ScanSample,
  type ScannerModeId,
} from '../lib/barcode-scanner-policy';

type CameraScanStatus = 'idle' | 'starting' | 'scanning' | 'detected' | 'error';

interface BarcodeCameraScanDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onDetected: (barcode: string) => void;
}

function readPersistedScannerMode(): ScannerModeId {
  if (typeof window === 'undefined') return 'store-labels';

  const value = window.localStorage.getItem(SCANNER_MODE_STORAGE_KEY);
  return isScannerModeId(value) ? value : 'store-labels';
}

function persistScannerMode(modeId: ScannerModeId): void {
  window.localStorage.setItem(SCANNER_MODE_STORAGE_KEY, modeId);
}

function needsSecureContextForCamera(): boolean {
  return window.isSecureContext === false;
}

export function BarcodeCameraScanDialog({
  open,
  onOpenChange,
  onDetected,
}: BarcodeCameraScanDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const quaggaTargetRef = useRef<HTMLDivElement | null>(null);
  const quaggaDetectedHandlerRef = useRef<((result: QuaggaJSResultObject) => void) | null>(null);
  const quaggaProcessedHandlerRef = useRef<((result: QuaggaJSResultObject) => void) | null>(null);
  const quaggaInitTimeoutRef = useRef<number | null>(null);
  const samplesRef = useRef<readonly ScanSample[]>([]);
  const processedResultsRef = useRef<WeakSet<QuaggaJSResultObject>>(new WeakSet());
  const scanSessionRef = useRef(0);
  const detectedRef = useRef(false);
  const onDetectedRef = useRef(onDetected);
  const onOpenChangeRef = useRef(onOpenChange);
  const [quaggaTargetElement, setQuaggaTargetElement] = useState<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<CameraScanStatus>('idle');
  const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);
  const [scannerModeId, setScannerModeId] = useState<ScannerModeId>(() =>
    readPersistedScannerMode(),
  );
  const [sampleProgress, setSampleProgress] = useState<{ count: number; required: number } | null>(
    null,
  );
  const scannerMode = getScannerMode(scannerModeId);

  useEffect(() => {
    onDetectedRef.current = onDetected;
    onOpenChangeRef.current = onOpenChange;
  }, [onDetected, onOpenChange]);

  const assignQuaggaTargetRef = useCallback((node: HTMLDivElement | null) => {
    quaggaTargetRef.current = node;
    setQuaggaTargetElement(node);
  }, []);

  const changeScannerMode = useCallback((modeId: ScannerModeId) => {
    persistScannerMode(modeId);
    setScannerModeId(modeId);
  }, []);

  const isActiveScanSession = useCallback(
    (sessionId: number) => scanSessionRef.current === sessionId && !detectedRef.current,
    [],
  );

  const stopScanning = useCallback((invalidateSession = true) => {
    if (invalidateSession) {
      scanSessionRef.current += 1;
    }

    if (quaggaInitTimeoutRef.current !== null) {
      window.clearTimeout(quaggaInitTimeoutRef.current);
      quaggaInitTimeoutRef.current = null;
    }

    if (quaggaDetectedHandlerRef.current) {
      Quagga.offDetected(quaggaDetectedHandlerRef.current);
      quaggaDetectedHandlerRef.current = null;
    }

    if (quaggaProcessedHandlerRef.current) {
      Quagga.offProcessed(quaggaProcessedHandlerRef.current);
      quaggaProcessedHandlerRef.current = null;
    }

    samplesRef.current = [];
    processedResultsRef.current = new WeakSet();
    setSampleProgress(null);
    void Quagga.stop().catch(() => undefined);
  }, []);

  const finishWithBarcode = useCallback(
    (rawBarcode: string) => {
      const normalized = normalizeBarcode(rawBarcode);
      if (!normalized || detectedRef.current) return;

      detectedRef.current = true;
      setStatus('detected');
      stopScanning();
      onDetectedRef.current(rawBarcode);
      onOpenChangeRef.current(false);
    },
    [stopScanning],
  );

  const startScanning = useCallback(async () => {
    const target = quaggaTargetElement;
    if (!target) return;

    if (needsSecureContextForCamera()) {
      setStatus('error');
      setErrorKey('admin.barcode.scan.cameraSecureContextRequired');
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('error');
      setErrorKey('admin.barcode.scan.cameraUnsupported');
      return;
    }

    stopScanning();
    detectedRef.current = false;
    samplesRef.current = [];
    processedResultsRef.current = new WeakSet();
    const sessionId = scanSessionRef.current + 1;
    scanSessionRef.current = sessionId;
    setStatus('starting');
    setErrorKey(null);
    setSampleProgress(null);

    const config = buildQuaggaConfig(target, scannerMode);

    const handleQuaggaResult = (result: QuaggaJSResultObject) => {
      if (!isActiveScanSession(sessionId)) return;
      if (processedResultsRef.current.has(result)) return;
      processedResultsRef.current.add(result);

      const now = window.performance.now();
      const analysis = analyzeQuaggaResult(result, now, scannerMode.allowedFormats);
      if (!analysis.sample) {
        return;
      }

      const nextSamples = addSample(samplesRef.current, analysis.sample, now);
      samplesRef.current = nextSamples;
      const nextProgress = getBestSampleProgress(nextSamples);
      setSampleProgress(nextProgress);

      const dominantSample = getDominantSample(nextSamples);
      if (dominantSample) {
        finishWithBarcode(dominantSample.raw);
      }
    };

    await new Promise<void>((resolve, reject) => {
      quaggaInitTimeoutRef.current = window.setTimeout(() => {
        quaggaInitTimeoutRef.current = null;
        if (!isActiveScanSession(sessionId)) {
          resolve();
          return;
        }

        void Quagga.init(config, (error: unknown) => {
          if (!isActiveScanSession(sessionId)) {
            void Quagga.stop().catch(() => undefined);
            resolve();
            return;
          }

          if (error) {
            reject(error);
            return;
          }

          const detectedHandler = (result: QuaggaJSResultObject) => handleQuaggaResult(result);
          const processedHandler = (result: QuaggaJSResultObject) => handleQuaggaResult(result);

          quaggaDetectedHandlerRef.current = detectedHandler;
          quaggaProcessedHandlerRef.current = processedHandler;
          Quagga.onDetected(detectedHandler);
          Quagga.onProcessed(processedHandler);
          Quagga.start();
          setStatus('scanning');
          resolve();
        });
      }, 1);
    }).catch(() => {
      if (!isActiveScanSession(sessionId)) return;
      stopScanning();
      setStatus('error');
      setErrorKey('admin.barcode.scan.cameraDecodeFailed');
    });
  }, [finishWithBarcode, isActiveScanSession, quaggaTargetElement, scannerMode, stopScanning]);

  useEffect(() => {
    if (!open) {
      setStatus('idle');
      setErrorKey(null);
      setSampleProgress(null);
      detectedRef.current = false;
      stopScanning();
      return;
    }

    if (!quaggaTargetElement) return;
    void startScanning();
    return stopScanning;
  }, [open, quaggaTargetElement, startScanning, stopScanning]);

  const statusLabel = sampleProgress
    ? t('admin.barcode.scan.cameraConsensusProgress', {
        count: sampleProgress.count,
        required: sampleProgress.required,
      })
    : status === 'starting'
      ? t('admin.barcode.scan.cameraStatusStarting')
      : status === 'scanning'
        ? t('admin.barcode.scan.cameraStatusScanning')
        : status === 'detected'
          ? t('admin.barcode.scan.cameraDetected')
          : t('admin.barcode.scan.cameraIdle');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir={dir}
        className="max-h-[90dvh] max-w-2xl overflow-y-auto"
        data-testid="barcode-camera-dialog"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="flex items-center gap-2 text-start">
              <Camera className="text-zaatar size-5" aria-hidden />
              {t('admin.barcode.scan.cameraTitle')}
            </DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-me-2 -mt-2"
              onClick={() => onOpenChange(false)}
              aria-label={t('admin.barcode.scan.cameraClose')}
              data-testid="barcode-camera-close"
            >
              <X className="size-5" aria-hidden />
            </Button>
          </div>
          <DialogDescription className="text-start">
            {t('admin.barcode.scan.cameraDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div
            className="border-border bg-muted/40 grid grid-cols-1 gap-1 rounded-xl border p-1 sm:grid-cols-3"
            role="radiogroup"
            aria-label={t('admin.barcode.scan.modeGroupLabel')}
          >
            {SCANNER_MODES.map((mode) => (
              <button
                key={mode.id}
                type="button"
                role="radio"
                aria-checked={scannerModeId === mode.id}
                data-testid={`barcode-camera-mode-${mode.id}`}
                onClick={() => changeScannerMode(mode.id)}
                className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  scannerModeId === mode.id
                    ? 'bg-admin-lime text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-card hover:text-foreground'
                }`}
              >
                {t(mode.labelKey)}
              </button>
            ))}
          </div>
          <p
            className="text-muted-foreground text-start text-sm"
            data-testid="barcode-camera-mode-description"
          >
            {t(scannerMode.descriptionKey)}
          </p>
        </div>

        <div className="relative aspect-[3/4] overflow-hidden rounded-xl border bg-black sm:aspect-[4/3]">
          <div
            ref={assignQuaggaTargetRef}
            className="absolute inset-0 overflow-hidden [&_canvas]:absolute [&_canvas]:inset-0 [&_canvas]:h-full [&_canvas]:w-full [&_video]:h-full [&_video]:w-full [&_video]:object-cover"
            data-testid="barcode-camera-quagga-target"
            aria-label={t('admin.barcode.scan.cameraVideoLabel')}
          />
          <div
            className="pointer-events-none absolute inset-x-[8%] top-1/2 h-24 -translate-y-1/2 rounded-xl border-2 border-white/90 shadow-[0_0_0_999px_rgb(0_0_0/0.35)]"
            aria-hidden
          />
        </div>

        <div className="min-h-6 text-start" aria-live="polite">
          {errorKey ? (
            <p role="alert" className="text-destructive text-sm" data-testid="barcode-camera-error">
              {t(errorKey)}
            </p>
          ) : (
            <p className="text-muted-foreground text-sm" data-testid="barcode-camera-status">
              {status === 'starting' ? (
                <Loader2 className="me-2 inline size-4 animate-spin" aria-hidden />
              ) : null}
              {statusLabel}
            </p>
          )}
        </div>

        {status === 'error' ? (
          <DialogFooter>
            <Button
              type="button"
              onClick={() => void startScanning()}
              data-testid="barcode-camera-retry"
            >
              <RefreshCw className="me-2 size-4" aria-hidden />
              {t('admin.barcode.scan.cameraTryAgain')}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
